import { z } from 'zod';
import { CARD_COUNT } from './cards';
import { MODES, MODE_LABELS, TOPICS } from './topics';
import { MBTI_TYPES } from './mbti';
import { THEMES } from './theme';
import { SPREADS } from './spreads';
import { NICKNAME_MAX, findBannedWord } from './nickname';

/** API/WS 페이로드 검증용 zod 스키마. 프론트·백엔드·DO 공용 */

/** 고민 한 줄 최대 길이 — 입력창 maxLength와 서버 검증이 같은 값을 쓴다 */
export const CONCERN_MAX = 200;

/** 스프레드 최대 장수 = 가장 큰 스프레드(켈틱 크로스 10장) — 스프레드를 추가하면 자동으로 따라온다 */
const MAX_SPREAD_CARDS = Math.max(...Object.values(SPREADS).map((s) => s.positions.length));
/** 방 최대 인원 = 모드별 maxSeats 중 최대(우정 4명) */
const MAX_SEATS = Math.max(...Object.values(MODE_LABELS).map((m) => m.maxSeats));

export const drawnCardSchema = z.object({
  cardId: z
    .number()
    .int()
    .min(0)
    .max(CARD_COUNT - 1),
  reversed: z.boolean(),
  position: z
    .number()
    .int()
    .min(0)
    .max(MAX_SPREAD_CARDS - 1),
});

export const topicSchema = z.enum(TOPICS);
export const modeSchema = z.enum(MODES);
export const themeSchema = z.enum(THEMES);
/** MBTI는 선택 입력 — 빈 문자열('설정 안 함')도 허용해 해제할 수 있게 한다 */
export const mbtiSchema = z.enum(MBTI_TYPES);
export const mbtiSettingSchema = z.union([mbtiSchema, z.literal('')]);
export const spreadIdSchema = z.enum(Object.keys(SPREADS) as [keyof typeof SPREADS]);

/** 닉네임 — 정책은 nickname.ts 단일 출처 */
export const nicknameSchema = z
  .string()
  .trim()
  .min(1, '닉네임을 입력해 주세요')
  .max(NICKNAME_MAX, `닉네임은 ${NICKNAME_MAX}자 이하로 지어주세요`)
  .refine((n) => !findBannedWord(n), '사용할 수 없는 단어가 들어있어요');

/** 참여자 1명 분 */
export const participantDrawSchema = z.object({
  seat: z
    .number()
    .int()
    .min(0)
    .max(MAX_SEATS - 1),
  nickname: nicknameSchema,
  cards: z.array(drawnCardSchema).min(1).max(MAX_SPREAD_CARDS),
  /** 선택 입력 — 해석 말투 개인화에만 사용. 리딩 기록에는 저장하지 않음 */
  mbti: mbtiSchema.optional(),
});

/** POST /ai/interpret 요청 */
export const interpretRequestSchema = z.object({
  mode: modeSchema,
  topic: topicSchema,
  spreadId: spreadIdSchema.default('three-card'),
  participants: z.array(participantDrawSchema).min(1).max(MAX_SEATS),
  concern: z.string().trim().max(CONCERN_MAX).optional(),
});
export type InterpretRequest = z.infer<typeof interpretRequestSchema>;

/**
 * GET /config 응답 — 클라이언트가 **런타임에** 받아야 하는 값들.
 *
 * 왜 빌드에 박지 않는가: 미니앱은 번들을 바꿀 때마다 **영업일 1~2일 심사**를 다시 받는다.
 * 광고 그룹 ID를 번들에 박아 두면 광고를 켜고 끄는 데 그 사이클이 통째로 든다.
 * 서버가 내려주면 wrangler var 수정 + API 배포(몇 분)로 끝난다.
 */
export interface ClientConfig {
  /** 앱인토스 리워드 광고 그룹 ID. 빈 문자열이면 광고를 띄우지 않는다 */
  tossAdGroupId: string;
  /**
   * 앱인토스 **배너(디스플레이)** 광고 그룹 ID. 빈 문자열이면 배너를 붙이지 않는다.
   *
   * 리워드와 **별도 그룹**이다 — 콘솔에서 따로 만들어야 하고 ID도 다르다.
   * 개발 중에는 테스트 ID(`ait-ad-test-banner-id`)를 쓸 수 있다.
   */
  tossBannerAdGroupId: string;
  /**
   * 웹에서 미니앱으로 보낼 때 쓰는 **토스 공유 링크**(`https://minion.…`). 빈 문자열이면
   * `intoss://` 딥링크로 연다 — 토스가 깔린 기기에서만 열린다.
   *
   * 이 링크는 토스가 설치 여부를 판단해 미니앱이나 앱스토어로 보낸다. 웹에서 직접 만들 수 없어
   * (SDK가 토스 안에서만 돈다) 미니앱에서 한 번 만들어 둔 것을 설정으로 받는다.
   * 목적지는 링크를 만들 때 고정된다 — 미니앱 **홈**을 가리키는 링크를 넣을 것.
   */
  tossEntryLink: string;
}

/**
 * POST /auth/toss/login 요청 — 미니앱에서 `appLogin()`이 돌려준 값을 그대로 넘긴다.
 * 인가 코드는 10분·1회용이라 서버가 곧바로 교환한다.
 */
export const tossLoginSchema = z.object({
  authorizationCode: z.string().min(1).max(2048),
  referrer: z.string().min(1).max(32),
});
export type TossLoginRequest = z.infer<typeof tossLoginSchema>;

/** POST /rooms 요청 */
export const createRoomSchema = z.object({
  mode: z.enum(['couple', 'friends']),
  topic: topicSchema,
  maxSeats: z.number().int().min(2).max(MAX_SEATS),
  /** 방장이 적는 고민 한 줄(선택) — 전원 해석에 함께 반영 */
  concern: z.string().trim().max(CONCERN_MAX).optional(),
});
export type CreateRoomRequest = z.infer<typeof createRoomSchema>;

/** POST /readings/share 요청 — 결과를 공유 링크로 발행 (비회원 가능) */
export const shareReadingSchema = z.object({
  mode: modeSchema,
  topic: topicSchema,
  spreadId: spreadIdSchema,
  participants: z.array(participantDrawSchema).min(1).max(MAX_SEATS),
  interpretation: z.string().min(20).max(8000),
  model: z.string().max(60).optional(),
  /**
   * 이 해석에 쓰인 토큰 수 (선택). 클라이언트가 스트림 완료 통지에서 받은 값을 그대로 전달한다.
   * 클라이언트가 보내는 값이라 신뢰 대상은 아니고 히스토리 표시·참고용이다.
   * 비용 집계의 근거는 서버가 직접 기록하는 stats_daily의 tokens:in/out 쪽이다.
   */
  tokensIn: z.number().int().nonnegative().max(1_000_000).optional(),
  tokensOut: z.number().int().nonnegative().max(1_000_000).optional(),
  /** true = 해석 완료 시 히스토리 자동 저장 (회원). 공유 버튼 클릭과 통계 구분용 */
  auto: z.boolean().optional(),
});
export type ShareReadingRequest = z.infer<typeof shareReadingSchema>;

/** GET /readings/shared/:id 응답 */
export interface SharedReading {
  mode: z.infer<typeof modeSchema>;
  topic: z.infer<typeof topicSchema>;
  spreadId: z.infer<typeof spreadIdSchema>;
  participants: { seat: number; nickname: string; cards: z.infer<typeof drawnCardSchema>[] }[];
  interpretation: string;
  createdAt: number;
}
